import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { useEffect, useState } from 'react';

const BAND_COLORS = {
    Early: '#e93e36',
    Emerging: '#f4b847',
    Progressing: '#7fb8a8',
    'Investment-ready': '#4aa0a4',
};

function bandFor(score) {
    if (score < 30) return 'Early';
    if (score < 55) return 'Emerging';
    if (score < 75) return 'Progressing';
    return 'Investment-ready';
}

export default function ScoreGauge({ score = 0, size = 280, animated = true, label }) {
    const stroke = 14;
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius * 0.78; // 78% arc, C-shape
    const gap = 2 * Math.PI * radius * 0.22;

    const band = bandFor(score);
    const color = BAND_COLORS[band];

    const count = useMotionValue(0);
    const rounded = useTransform(count, (v) => Math.round(v));
    const [display, setDisplay] = useState(0);

    useEffect(() => {
        if (!animated) {
            setDisplay(score);
            return;
        }
        const controls = animate(count, score, { duration: 1.6, ease: 'easeOut' });
        const unsub = rounded.on('change', (v) => setDisplay(v));
        return () => {
            controls.stop();
            unsub();
        };
    }, [score, animated, count, rounded]);

    const arcTargetLength = (score / 100) * circumference;

    return (
        <div className="flex flex-col items-center" data-testid="score-gauge">
            <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                style={{ transform: 'rotate(130deg)' }}
                aria-label={`Funding readiness score: ${score} out of 100`}
            >
                {/* Background track */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="#e0d9c8"
                    strokeWidth={stroke}
                    strokeLinecap="round"
                    strokeDasharray={`${circumference} ${gap}`}
                />
                {/* Progress arc */}
                <motion.circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={color}
                    strokeWidth={stroke}
                    strokeLinecap="round"
                    initial={{ strokeDasharray: `0 ${circumference + gap}` }}
                    animate={{ strokeDasharray: `${arcTargetLength} ${circumference + gap}` }}
                    transition={{ duration: animated ? 1.6 : 0, ease: 'easeOut' }}
                />
            </svg>
            <div className="-mt-[65%] flex flex-col items-center pointer-events-none">
                <div className="text-overline text-weave-ink-soft" data-testid="gauge-label">
                    {label || 'Funding Readiness'}
                </div>
                <div
                    className="font-display text-7xl text-weave-ink leading-none mt-2"
                    data-testid="gauge-score"
                >
                    {display}
                </div>
                <div className="text-overline text-weave-ink-soft mt-1">out of 100</div>
                <div
                    className="mt-4 px-4 py-1.5 rounded-full text-sm font-medium"
                    style={{ backgroundColor: `${color}22`, color }}
                    data-testid="gauge-band"
                >
                    {band}
                </div>
            </div>
        </div>
    );
}
